import assert from 'minimalistic-assert'

import { isForwarderContractCached } from './eth-like-util.js'
import { EXTRA_PERCENTAGE, fetchGasLimit, resolveDefaultTxInput } from './gas-estimation.js'
import { getFeeFactory } from './get-fee.js'
import { getNftArguments } from './nft-utils.js'

const FIXED_TRANSFER_GAS_LIMIT_ASSETS = new Set([
  'amp',
  'tetherusd',
  'usdcoin',
  'snx',
  'geminidollar',
])

const UNKNOWN_ADDRESS_EXTRA_PERCENTAGE = {
  usdt_bsc_ddedf0f8: 80,
}

export async function resolveExtraPercentage({ asset, fromAddress, toAddress }) {
  if (asset.baseAsset.estimateL1DataFee) {
    return 100
  }

  const isToken = asset.name !== asset.baseAsset.name

  const isFixedGasLimitToken = isToken && FIXED_TRANSFER_GAS_LIMIT_ASSETS.has(asset.name)

  if (isFixedGasLimitToken) {
    return 0
  }

  // calling forwarder contracts with a bumped gas limit causes 'Out Of Gas' error on chain
  const hasForwarderContract =
    !isToken && toAddress ? await isForwarderContractCached({ asset, address: toAddress }) : false

  if (hasForwarderContract) {
    return 0
  }

  if (!fromAddress || !toAddress) {
    return UNKNOWN_ADDRESS_EXTRA_PERCENTAGE[asset.name] ?? EXTRA_PERCENTAGE
  }

  return EXTRA_PERCENTAGE
}

const ARBITRARY_ADDRESS = '0xffffffffffffffffffffffffffffffffffffffff'

const getFeeAsyncFactory = ({
  assetClientInterface,
  gasLimit: defaultGasLimit,
  createUnsignedTx,
}) => {
  assert(assetClientInterface, 'assetClientInterface is required')
  assert(createUnsignedTx, 'createUnsignedTx is required')
  const getFee = getFeeFactory({ gasLimit: defaultGasLimit })
  return async ({
    nft,
    asset,
    fromAddress: providedFromAddress,
    toAddress: providedToAddress,
    amount,
    bip70,
    txInput: txInputPram,
    isExchange,
    customFee,
    calculateEffectiveFee,
    isSendAll,
    gasLimit: providedGasLimit,
    isRbfAllowed = true, // Destkop, isRbfAllowed=true when advanced panel is on
  }) => {
    const fromAddress = providedFromAddress || ARBITRARY_ADDRESS // sending from a random address
    const toAddress = providedToAddress || ARBITRARY_ADDRESS // sending to a random address,

    const resolveGasLimit = async () => {
      if (nft) {
        return getNftArguments({ asset, nft, fromAddress, toAddress })
      }

      if (!amount) amount = asset.currency.ZERO
      const txInput = txInputPram || resolveDefaultTxInput({ asset, toAddress, amount })
      if (providedGasLimit) return { gasLimit: providedGasLimit, txInput }

      const extraPercentage = await resolveExtraPercentage({
        asset,
        fromAddress: providedFromAddress,
        toAddress: providedToAddress,
      })

      const gasLimit = await fetchGasLimit({
        asset,
        fromAddress,
        toAddress,
        txInput,
        amount,
        bip70,
        extraPercentage,
      })
      return { gasLimit, txInput }
    }

    const { txInput, gasLimit, contractAddress } = await resolveGasLimit()

    const feeData = await assetClientInterface.getFeeConfig({ assetName: asset.baseAsset.name })

    const { fee, gasPrice, ...rest } = getFee({
      asset,
      feeData,
      gasLimit,
      isExchange,
      isSendAll,
      amount,
      isRbfAllowed,
      calculateEffectiveFee,
      customFee,
    })

    const optimismL1DataFee = asset.baseAsset.estimateL1DataFee
      ? await asset.baseAsset.estimateL1DataFee({
          unsignedTx: createUnsignedTx({
            asset,
            address: contractAddress || toAddress,
            fromAddress,
            amount,
            nonce: 0,
            txInput,
            gasPrice,
            gasLimit,
          }),
        })
      : undefined

    const l1DataFee = optimismL1DataFee
      ? asset.baseAsset.currency.baseUnit(optimismL1DataFee)
      : asset.baseAsset.currency.ZERO
    return {
      fee: fee.add(l1DataFee),
      optimismL1DataFee,
      gasLimit,
      gasPrice,
      ...rest,
    }
  }
}

export default getFeeAsyncFactory
